import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import Button from 'v3/components/Button';
import { controlTowerService, errorMessage } from './service';

const eventTypes = {
  REFUSAL: { label: 'Recusa', tone: 'refusal' },
  ACCEPTANCE: { label: 'Aceite', tone: 'acceptance' },
  REVERSAL: { label: 'Reversão', tone: 'reversal' },
};
const links = {
  FROTA: 'Frota',
  OURO: 'Ouro',
  AGREGADO_CARRETA_COOPER: 'Agregado Carreta Cooper',
  AGREGADO_CONJUNTO_COMPLETO: 'Agregado Conjunto Completo',
  TERCEIRO: 'Terceiro',
};
const dateFormat = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});
const dateLabel = (value) => dateFormat.format(new Date(value));
const shortFormat = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
/** "08/10 11:28" no ano corrente; "08/10/25 11:28" nos anteriores (horário de São Paulo). */
function shortLabel(value) {
  const parts = Object.fromEntries(
    shortFormat.formatToParts(new Date(value)).map(({ type, value: part }) => [type, part]),
  );
  const thisYear = Object.fromEntries(
    shortFormat.formatToParts(new Date()).map(({ type, value: part }) => [type, part]),
  ).year;
  const day = `${parts.day}/${parts.month}${parts.year === thisYear ? '' : `/${parts.year}`}`;
  return `${day} ${parts.hour}:${parts.minute}`;
}
const linkLabel = (event) =>
  `${links[event.linkType]}${
    event.linkType === 'AGREGADO_CARRETA_COOPER'
      ? event.corridor
        ? ' · Corredor'
        : ' · Normal'
      : ''
  }`;
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const positiveId = (value) => Number.isSafeInteger(value) && value > 0;

function validatePage(page, type, id, cursor) {
  const invalid = () => {
    throw new Error('Resposta inválida ao consultar o histórico.');
  };
  if (!Array.isArray(page?.data) || typeof page?.meta?.hasMore !== 'boolean') invalid();
  if (
    page.meta.hasMore &&
    (!page.data.length ||
      typeof page.meta.nextCursor !== 'string' ||
      !page.meta.nextCursor ||
      page.meta.nextCursor === cursor)
  )
    invalid();
  for (const event of page.data) {
    if (
      !positiveId(event?.id) ||
      !Object.hasOwn(eventTypes, event.eventType) ||
      !Object.hasOwn(links, event.linkType) ||
      (event.linkType === 'AGREGADO_CARRETA_COOPER' && typeof event.corridor !== 'boolean') ||
      event.asset?.type !== type ||
      event.asset?.id !== Number(id) ||
      typeof event.occurredAt !== 'string' ||
      !Number.isFinite(Date.parse(event.occurredAt)) ||
      new Date(event.occurredAt).toISOString() !== event.occurredAt ||
      !positiveId(event.load?.id) ||
      !['LOAD', 'PRE_LOAD'].includes(event.load?.kind) ||
      !positiveId(event.actor?.id) ||
      typeof event.actor?.name !== 'string' ||
      (event.eventType === 'REVERSAL' && !positiveId(event.originalRefusalId)) ||
      (event.eventType !== 'ACCEPTANCE' && typeof event.refusalReason?.text !== 'string')
    )
      invalid();
  }
  return page;
}

const Container = styled.div`
  font-size: 14px;
  min-width: 0;
  color: #1f2933;
  .tc-history-heading {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .tc-history-heading h3 {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 14px;
  }
  .tc-history-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 4px 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .tc-history-counts {
    font-weight: 400;
    color: #606775;
  }
  .tc-history-icon {
    display: inline-flex;
    padding: 4px;
    border: 0;
    border-radius: 4px;
    background: none;
    color: #606775;
    cursor: pointer;
    &:disabled {
      cursor: default;
      opacity: 0.5;
    }
  }
  .tc-history-toggle:focus-visible,
  .tc-history-icon:focus-visible,
  .tc-history-scroll:focus-visible {
    outline: 2px solid #2475b4;
    outline-offset: 2px;
  }
  .tc-history-scroll {
    max-height: 320px;
    margin-top: 6px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 0;
    border-top: 1px solid #edf0f3;
  }
  .tc-event-tag {
    flex-shrink: 0;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }
  .tc-event-refusal {
    color: #b42a2a;
    background: #fde8e8;
  }
  .tc-event-acceptance {
    color: #1d6b3a;
    background: #e3f4e8;
  }
  .tc-event-reversal {
    color: #1c4b75;
    background: #e2eefa;
  }
  .tc-event-text {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .tc-event-muted,
  li time {
    color: #8a929c;
  }
  li time {
    flex-shrink: 0;
    font-size: 13px;
  }
  .tc-event-reverted {
    color: #8a929c;
  }
  .tc-event-reverted .tc-event-tag {
    opacity: 0.75;
  }
  .tc-history-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0 0;
    font-size: 12px;
    color: #606775;
  }
  .tc-history-status {
    margin: 8px 0 0;
    color: #606775;
  }
  .tc-history-error {
    margin: 8px 0 0;
    color: #ba2525;
  }
  @media (max-width: 520px) {
    li {
      flex-wrap: wrap;
      gap: 4px 10px;
    }
    li time {
      margin-left: auto;
    }
    .tc-event-text {
      flex-basis: 100%;
      order: 3;
    }
  }
`;

export default function History({ type, id }) {
  const titleId = useId();
  const [events, setEvents] = useState([]);
  const [meta, setMeta] = useState({ hasMore: false, nextCursor: null });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const request = useRef({ sequence: 0, busy: false, controller: null });

  async function load(reset = true) {
    if (request.current.busy) return;
    const ticket = ++request.current.sequence;
    const controller = new AbortController();
    request.current.controller = controller;
    request.current.busy = true;
    setBusy(true);
    setError(null);
    const cursor = reset ? null : meta.nextCursor;
    try {
      const response = await controlTowerService.history(type, id, cursor, controller.signal);
      if (ticket !== request.current.sequence) return;
      const page = validatePage(response, type, id, cursor);
      setEvents((previous) => {
        const merged = reset ? [] : [...previous];
        const ids = new Set(merged.map((event) => event.id));
        for (const event of page.data) {
          if (!ids.has(event.id)) {
            merged.push(event);
            ids.add(event.id);
          }
        }
        return merged;
      });
      setMeta(page.meta);
      setLoaded(true);
    } catch (failure) {
      if (ticket === request.current.sequence && !controller.signal.aborted)
        setError({ message: errorMessage(failure), reset });
    } finally {
      if (ticket === request.current.sequence) {
        request.current.busy = false;
        setBusy(false);
      }
    }
  }

  useEffect(() => {
    load();
    return () => {
      request.current.sequence += 1;
      request.current.controller?.abort();
      request.current.busy = false;
    };
  }, []); // A instância recebe key por tipo/ID; a troca de empresa remonta o card pai.

  const reversals = new Map(
    events
      .filter((event) => event.eventType === 'REVERSAL')
      .map((event) => [event.originalRefusalId, event]),
  );
  const activeRefusals = events.filter(
    (event) => event.eventType === 'REFUSAL' && !reversals.has(event.id),
  ).length;
  // Usuário e vínculo iguais em todos os eventos vão para o rodapé; diferentes, para cada linha.
  const authors = new Set(events.map((event) => `${event.actor.name}\n${linkLabel(event)}`));
  const sharedAuthor = authors.size === 1 ? events[0] : null;
  const [open, setOpen] = useState(true);
  const listId = useId();

  return (
    <Container>
      <div className="tc-history-heading">
        <h3 id={titleId}>
          <button
            type="button"
            className="tc-history-toggle"
            aria-expanded={open}
            aria-controls={listId}
            onClick={() => setOpen((value) => !value)}
          >
            <span>
              Recusas e aceites
              <span className="tc-history-counts">
                {loaded
                  ? ` · ${plural(activeRefusals, 'recusa ativa', 'recusas ativas')} · ${
                      meta.hasMore
                        ? plural(events.length, 'evento carregado', 'eventos carregados')
                        : plural(events.length, 'evento', 'eventos')
                    }`
                  : ''}
              </span>
            </span>
            {open ? (
              <ChevronUp size={18} aria-hidden="true" />
            ) : (
              <ChevronDown size={18} aria-hidden="true" />
            )}
          </button>
        </h3>
        <button
          type="button"
          className="tc-history-icon"
          disabled={busy}
          onClick={() => load()}
          aria-label="Atualizar histórico"
          title="Atualizar histórico"
        >
          <RefreshCw size={15} aria-hidden="true" />
        </button>
      </div>
      <div id={listId} hidden={!open}>
        {busy && (
          <p role="status" className="tc-history-status">
            {loaded ? 'Carregando eventos do histórico...' : 'Carregando histórico...'}
          </p>
        )}
        {error && (
          <div className="tc-history-error" role="alert">
            <p>Não foi possível carregar o histórico. {error.message}</p>
            <Button variant="secondary" size="sm" onClick={() => load(error.reset)}>
              Tentar histórico novamente
            </Button>
          </div>
        )}
        {loaded && !events.length && !busy && !error && (
          <p role="status" className="tc-history-status">
            Nenhum registro de recusa, aceite ou reversão para esta placa.
          </p>
        )}
        {!!events.length && (
          <div className="tc-history-scroll" role="region" aria-labelledby={titleId} tabIndex={0}>
            <ol aria-label="Eventos da placa">
              {events.map((event) => {
                const action = eventTypes[event.eventType];
                const reversal = event.eventType === 'REFUSAL' ? reversals.get(event.id) : null;
                return (
                  <li
                    key={event.id}
                    data-event-id={event.id}
                    className={reversal ? 'tc-event-reverted' : undefined}
                  >
                    <span className={`tc-event-tag tc-event-${action.tone}`}>
                      {action.label} #{event.id}
                    </span>
                    <span className="tc-event-text">
                      {event.load.kind === 'PRE_LOAD' ? 'Pré-carga' : 'Carga'} #{event.load.id}
                      {event.refusalReason?.text && ` · ${event.refusalReason.text}`}
                      {event.eventType === 'REVERSAL' && (
                        <span className="tc-event-muted">
                          {' '}
                          · reverte #{event.originalRefusalId}
                        </span>
                      )}
                      {reversal && (
                        <span
                          className="tc-event-muted"
                          title={`Revertida por ${reversal.actor.name || 'usuário não informado'} em ${dateLabel(reversal.occurredAt)} (reversão #${reversal.id})`}
                        >
                          {' '}
                          · revertida
                        </span>
                      )}
                      {!sharedAuthor && (
                        <span className="tc-event-muted">
                          {' '}
                          · por {event.actor.name || 'usuário não informado'} · vínculo{' '}
                          {linkLabel(event)}
                        </span>
                      )}
                    </span>
                    <time dateTime={event.occurredAt} title={dateLabel(event.occurredAt)}>
                      {shortLabel(event.occurredAt)}
                    </time>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
        {loaded && !!events.length && (
          <div className="tc-history-footer">
            <span>
              {sharedAuthor
                ? `Por ${sharedAuthor.actor.name || 'usuário não informado'} · vínculo ${linkLabel(sharedAuthor)} · horário de São Paulo`
                : 'Horário de São Paulo'}
            </span>
            {meta.hasMore && !error && (
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => load(false)}>
                Carregar mais
              </Button>
            )}
          </div>
        )}
      </div>
    </Container>
  );
}
